import { ModalStep } from "@/components/Evaluate/TableComponents/NewColumnModal/ModalRouter";
import { MCPConfiguration } from "@/types/evaluate";
import { Button } from "@/components/ui/button";
import { PlusIcon } from "@heroicons/react/solid";
import { useMemo, useRef, useState, useLayoutEffect } from "react";
import VariableList from "@/components/InputVariableConfigurator/VariableList";
import { Variable } from "@/components/InputVariableConfigurator/types";
import useAutoScroll from "@/components/InputVariableConfigurator/hooks/useAutoScroll";

const ServerConnectStep = ({
  navigateAway,
  configuration,
  setConfiguration,
  editable,
}: {
  navigateAway: () => void;
  configuration: Partial<MCPConfiguration>;
  setConfiguration: (
    data:
      | Partial<MCPConfiguration>
      | ((prevState: Partial<MCPConfiguration>) => Partial<MCPConfiguration>),
  ) => void;
  editable: boolean;
}) => {
  const [headers, setHeaders] = useState(
    configuration?.server_configuration?.headers
      ? Object.entries(configuration.server_configuration?.headers)
      : [],
  );
  const ref = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);

  const variables = useMemo(
    () =>
      headers.map((header, index) => ({
        key: header[0],
        value: header[1],
        id: `${index}-${header[0]}-${header[1]}`,
      })),
    [headers],
  );

  const { lastItemRef } = useAutoScroll(variables, ref);

  useLayoutEffect(() => {
    const scrollContainer = ref.current;
    if (scrollContainer) {
      scrollContainer.scrollTop = scrollPositionRef.current;
    }
  });

  const handleScroll = () => {
    if (ref.current) {
      scrollPositionRef.current = ref.current.scrollTop;
    }
  };

  const setServerUrl = (serverUrl: string) => {
    setConfiguration((config) => ({
      ...config,
      ...{
        server_configuration: {
          ...config.server_configuration,
          ...{
            server_url: serverUrl,
            headers: config.server_configuration?.headers || {},
          },
        },
      },
    }));
  };

  const updateHeaders = (
    func: (inp: [string, string][]) => [string, string][],
  ) => {
    setHeaders((prev) => {
      let newHeaders = func(prev);
      setConfiguration((config) => ({
        ...config,
        ...{
          server_configuration: {
            ...config.server_configuration,
            ...{
              server_url: config.server_configuration?.server_url || "",
              headers: Object.fromEntries(newHeaders),
            },
          },
        },
      }));
      return newHeaders;
    });
  };

  return (
    <ModalStep
      navigateNext={editable ? navigateAway : undefined}
      nextButtonText={editable ? "Save" : undefined}
      navigatePrevious={editable ? undefined : navigateAway}
    >
      <div className="grid grid-cols-[auto,1fr] gap-4">
        <h2 className="col-span-2 text-lg font-semibold">
          Configure MCP server.
        </h2>
        <label htmlFor="url" className="self-center text-left">
          Endpoint URL:
        </label>
        <div>
          <input
            id="url"
            className={`w-full rounded border border-gray-300 px-2 py-1 disabled:cursor-not-allowed disabled:bg-gray-50`}
            value={configuration?.server_configuration?.server_url || ""}
            placeholder="https://mpc.example.com/promptlayer"
            onChange={(e) => setServerUrl(e.target.value)}
            disabled={!editable}
          />
        </div>
        <label className="col-span-2 font-medium">Auth Headers</label>
        <div className="col-span-2 flex flex-col gap-y-2">
          <div
            ref={ref}
            className="max-h-[170px] overflow-y-auto"
            onScroll={handleScroll}
          >
            <VariableList
              variables={variables}
              placeholder="<API_KEY>"
              keyPlaceholder="X-API-Key"
              tooltip="Header value"
              keyTooltip="Header name"
              removeTooltip="Remove header"
              readOnly={!editable}
              updateInputVariable={(variable: Variable, index: number) => {
                const keyValue = String(variable.key);
                const stringValue =
                  typeof variable.value === "object"
                    ? JSON.stringify(variable.value)
                    : String(variable.value);

                updateHeaders((prev) => {
                  const newHeaders = [...prev];
                  newHeaders[index] = [keyValue, stringValue];
                  return newHeaders;
                });
              }}
              removeInputVariable={(index: number) => {
                updateHeaders((prev) => {
                  return prev.filter((_, i) => i !== index);
                });
              }}
              lastItemRef={lastItemRef}
            />
          </div>
          <Button
            variant="outline"
            className="w-full"
            onClick={() =>
              updateHeaders((prev) => {
                const newHeader: [string, string] = ["", ""];
                return [...prev, newHeader];
              })
            }
            disabled={!editable}
          >
            <PlusIcon className="mr-2 h-5 w-5 text-gray-600" />
            Add Header
          </Button>
        </div>
      </div>
    </ModalStep>
  );
};

export default ServerConnectStep;
